// Shared helpers for the Experiences module.
// NOTE: the underlying supplier is only referenced through the existing
// backend endpoints (`/tourapp/viator/...`). Nothing here should ever
// surface the supplier's name or link out to the supplier's own site -
// every screen and every "book" action stays inside our own app.

export const CART_STORAGE_KEY = "linsoa_experience_cart";
export const PRODUCT_CACHE_PREFIX = "linsoa_experience_product_";

export const formatDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export const formatDisplayDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

// Short range display used in the search bar / results filter chip, e.g.
// "Aug 18 - 20" (same month), "Jul 28 - Aug 3" (spanning months), or
// "Dec 29, 2026 - Jan 2, 2027" (spanning years).
export const formatDateRange = (start, end) => {
  if (!start || !end) return "";
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return "";

  const sameYear = s.getFullYear() === e.getFullYear();
  const sameMonth = sameYear && s.getMonth() === e.getMonth();

  if (sameMonth) {
    const month = s.toLocaleDateString("en-US", { month: "short" });
    return `${month} ${s.getDate()} - ${e.getDate()}`;
  }

  const startLabel = s.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
  });
  const endLabel = e.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return `${startLabel} - ${endLabel}`;
};

export const formatDuration = (minutes) => {
  if (!minutes) return null;
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hrs && mins) return `${hrs} hr ${mins} min`;
  if (hrs) return `${hrs} hr`;
  return `${mins} min`;
};

export const getDurationLabel = (product) => {
  if (!product) return null;
  const d = product.duration;
  if (!d) return null;
  if (d.fixedDurationInMinutes) return formatDuration(d.fixedDurationInMinutes);
  if (d.variableDurationFromMinutes && d.variableDurationToMinutes) {
    return `${formatDuration(d.variableDurationFromMinutes)} - ${formatDuration(
      d.variableDurationToMinutes
    )}`;
  }
  return null;
};

export const getCoverImage = (product, minWidth = 400) => {
  if (!product || !product.images || !product.images.length) return null;

  const cover = product.images.find((img) => img.isCover) || product.images[0];
  if (!cover || !cover.variants || !cover.variants.length) return null;

  const sorted = [...cover.variants].sort((a, b) => (a.width || 0) - (b.width || 0));
  const pick = sorted.find((v) => v.width >= minWidth) || sorted[sorted.length - 1];

  return pick ? pick.url : null;
};

export const getAllImages = (product, minWidth = 600) => {
  if (!product || !product.images || !product.images.length) return [];

  return product.images
    .map((img) => {
      if (!img.variants || !img.variants.length) return null;

      const sorted = [...img.variants].sort((a, b) => (a.width || 0) - (b.width || 0));
      const pick = sorted.find((v) => v.width >= minWidth) || sorted[sorted.length - 1];

      return pick ? pick.url : null;
    })
    .filter(Boolean);
};

export const getAllImagesNew = (images, minWidth = 300) => {
  if (!images || !images.length) return [];

  return images
    .map((img) => {


      const sorted = [...img.variants].sort((a, b) => (a.width || 0) - (b.width || 0));
      const pick = sorted.find((v) => v.width >= minWidth) || sorted[sorted.length - 1];

      return pick ? pick.url : null;
    })
    .filter(Boolean);
};

// ... keep your other utils here (formatMoney, getRating, etc.);

export const getRating = (product) =>
  product && product.reviews ? product.reviews.combinedAverageRating : null;

export const getReviewCount = (product) =>
  product && product.reviews ? product.reviews.totalReviews : null;

export const getFromPrice = (product) =>
  product && product.pricing && product.pricing.summary
    ? product.pricing.summary.fromPrice
    : null;

export const getCurrency = (product) =>
  product && product.pricing ? product.pricing.currency : "USD";

export const hasFreeCancellation = (product) =>
  !!(product && product.flags && product.flags.includes("FREE_CANCELLATION"));

// Kept deliberately simple and *consistent* across every card/page: whole
// numbers never grow a trailing ".00" (so a round INR price and a round AED
// price look the same shape), while genuinely fractional prices still show
// up to 2 decimals. This is what was making two product cards with
// different currencies look like they followed different rules.
const getCurrencyLocale = (currency = "USD") => {
  const localeMap = {
    AED: "en-AE",
    AUD: "en-AU",
    CAD: "en-CA",
    EUR: "en-IE",
    GBP: "en-GB",
    INR: "en-IN",
    SAR: "en-SA",
    USD: "en-US",
  };

  return localeMap[currency] || "en-US";
};

export const formatMoney = (amount, currency = "USD") => {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) return "";
  try {
    return new Intl.NumberFormat(getCurrencyLocale(currency), {
      style: "currency",
      currency: currency || "USD",
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (e) {
    return `${currency} ${Number(amount).toFixed(2)}`;
  }
};

// Cache a full product object locally (keyed by product code) so the
// details / cart / checkout pages can render without ever needing to call
// out to a third-party product page. This also makes a details page
// refresh-safe even though it was primarily opened via router state.
export const cacheProduct = (product) => {
  if (!product || !product.productCode) return;
  try {
    sessionStorage.setItem(
      `${PRODUCT_CACHE_PREFIX}${product.productCode}`,
      JSON.stringify(product)
    );
  } catch (e) {
    // ignore storage errors (private browsing, quota, etc.)
  }
};

export const getCachedProduct = (productCode) => {
  try {
    const raw = sessionStorage.getItem(`${PRODUCT_CACHE_PREFIX}${productCode}`);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};



export const getTop10RootTags = (tags) => {
  return tags
    // 1. Filter out tags that have parent categories (keep only root tags)
    .filter(tag => tag.parentTagIds.length === 0)

    // 2. Sort them by count in descending order (highest count first)
    .sort((a, b) => b.count - a.count)

    // 3. Take only the first 10 results
    .slice(0, 10);
}

